/**
 * Handles appointment booking when offline.
 * Returns an Observable that creates temp appointments, enqueues for sync,
 * and dispatches success so the BookingSuccessPage renders.
 *
 * @author Milton Filho
 */
import { Observable, of, concat } from 'rxjs';
import { AnyPayloadAction } from 'types/types';
import { StateObservable } from 'redux-observable';
import { GlobalState } from 'reducers';
import {
  BookAppointmentActionPayload,
  bookAppointmentFailure,
  bookAppointmentSuccess,
} from 'modules/appointments/actions';
import {
  enqueueOfflineItem,
  offlineBookAppointmentEnqueued,
} from 'modules/offlineSync/actions';
import { OfflineActionKind, OfflineQueueItem } from 'modules/offlineSync/types';
import { generateTempAppointments } from './generateTempAppointments';
import { getId } from 'selectors/user/getId';
import { getCaseForPatient } from 'selectors/cases/getCaseForPatient';
import toast from 'react-hot-toast';
import { randomUUID } from 'utils/crypto/randomUUID';

export const handleOfflineBooking = (
  payload: BookAppointmentActionPayload,
  state$: StateObservable<GlobalState>,
): Observable<AnyPayloadAction> => {
  const userId = getId(state$.value);
  const therapistId = payload.therapistId || Number(userId);

  // Resolve caseId from Redux if not provided
  let resolvedCaseId = payload.caseId;
  if (!resolvedCaseId) {
    const patientCase = getCaseForPatient(state$.value, {
      patientId: payload.patientId,
    });
    if (!patientCase) {
      toast.error('Cannot book offline without patient case data');
      return of(
        bookAppointmentFailure({
          name: 'OfflineError',
          message: 'Cannot book offline without patient case data',
        }),
      );
    }
    resolvedCaseId = patientCase.id;
  }

  const payloadWithCase: BookAppointmentActionPayload = {
    ...payload,
    caseId: resolvedCaseId,
  };

  const tempIdCounter = state$.value.offlineSync.tempIdCounter;
  const tempAppointments = generateTempAppointments(
    payloadWithCase,
    tempIdCounter,
    therapistId,
  );

  const tempIds = tempAppointments.map(a => a.id);

  const queueItem: OfflineQueueItem = {
    // **This id is also the `Idempotency-Key` sent to the API.**
    //
    // It has two jobs now, and the second one is a contract with the backend:
    //   the API recognises a repeat of one booking intent by this value, and
    //   resumes the write it already started instead of creating a second
    //   appointment. That works only because the id is minted here — where the
    //   therapist's intent is captured — persisted with the item, and identical
    //   on every replay of it.
    //
    // So anything that changes how these ids are produced changes what the
    //   backend can recognise. Per-intent and stable are the two properties
    //   that matter; a value derived from the attempt, or regenerated on
    //   replay, would leave the header in place and the guarantee gone. The
    //   tests that catch this live at the transmission end, in
    //   `epics/syncOnReconnect$.test.ts`, and would not fail here.
    //
    // See `utils/api/getIdempotencyHeaders`.
    id: randomUUID(),
    kind: OfflineActionKind.BookAppointment,
    payload: {
      app_datetime: payloadWithCase.dateTime,
      app_type: payloadWithCase.type,
      case_id: resolvedCaseId,
      duration: payloadWithCase.duration,
      patient_id: payloadWithCase.patientId,
      therapist_id: therapistId,
      user_id: userId,
      recurrence: payloadWithCase.recurrence,
      recurs_until: payloadWithCase.recursUntil,
    },
    tempAppointmentIds: tempIds,
    createdAt: new Date().toISOString(),
  };

  // Build BookAppointmentResponse for the success page
  const bookingResponse = {
    appointmentsCreated: tempAppointments.length,
    totalRequested: tempAppointments.length,
    appointments: tempAppointments,
    errors: [],
    recurrenceInfo: payloadWithCase.recurrence
      ? {
          interval: payloadWithCase.recurrence,
          startDate: payloadWithCase.dateTime,
          endDate: payloadWithCase.recursUntil || payloadWithCase.dateTime,
        }
      : undefined,
  };

  return concat(
    of(enqueueOfflineItem(queueItem)),
    of(offlineBookAppointmentEnqueued({ tempAppointments })),
    of(bookAppointmentSuccess(bookingResponse)),
  );
};
